import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  AssignmentCategory,
  AssignmentCoverImage,
  GeneralAssignmentItem,
} from '../../types/assignmentPointsNote';
import type { ClassMemberRow } from '../../utils/classMembersStorage';
import AssignmentCoverPickField from './AssignmentCoverPickField';
import AssignmentMemberPickField from './AssignmentMemberPickField';

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function shiftDateIso(iso: string, deltaDays: number): string {
  if (!iso) return todayIso();
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return todayIso();
  d.setDate(d.getDate() + deltaDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDueBadge(dueDate: string): string {
  if (!dueDate) return '';
  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return '';
  const dayLabel = ['일', '월', '화', '수', '목', '금', '토'][due.getDay()];
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dueOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round((dueOnly.getTime() - todayOnly.getTime()) / 86400000);
  let rel = '';
  if (diffDays === 0) rel = ' (오늘)';
  else if (diffDays > 0) rel = ` (+${diffDays})`;
  else rel = ` (${diffDays})`;
  return `${dayLabel}${rel}`;
}

export type FlatMember = ClassMemberRow & {
  orderIndex: number;
  groupId: string;
  groupName: string;
};

type Props = {
  open: boolean;
  mode: 'new' | 'edit' | 'copy';
  initial?: GeneralAssignmentItem | null;
  members: FlatMember[];
  /** 신규 시 기본 선택할 그룹 (현재 페이지 그룹) */
  defaultGroupId?: string | null;
  categories: AssignmentCategory[];
  defaultPoints: number;
  coverImages: AssignmentCoverImage[];
  onCoverImagesChange: (images: AssignmentCoverImage[]) => void;
  onClose: () => void;
  onSave: (item: GeneralAssignmentItem) => void;
};

function copyTitle(title: string): string {
  const t = (title || '제목 없음').trim() || '제목 없음';
  return t.includes('(복사)') ? t : `${t} (복사)`;
}

const IconReset = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const IconMinus = () => (
  <svg width="12" height="2" viewBox="0 0 12 2" fill="none" aria-hidden>
    <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IconPlus = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const GeneralAssignmentSettingsModal: React.FC<Props> = ({
  open,
  mode,
  initial,
  members,
  defaultGroupId = null,
  categories,
  defaultPoints,
  coverImages,
  onCoverImagesChange,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(todayIso());
  const [basePointsDisplay, setBasePointsDisplay] = useState(String(defaultPoints));
  const [categoryId, setCategoryId] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [coverImageId, setCoverImageId] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rewardEnabled, setRewardEnabled] = useState(false);
  const [rewardCoinsDisplay, setRewardCoinsDisplay] = useState('0');

  const getBasePointsNum = () =>
    basePointsDisplay === '' ? 0 : Number(basePointsDisplay) || 0;
  const getRewardCoinsNum = () =>
    rewardCoinsDisplay === '' ? 0 : Number(rewardCoinsDisplay) || 0;

  useEffect(() => {
    if (!open) return;
    setCategoryOpen(false);
    if ((mode === 'edit' || mode === 'copy') && initial) {
      setTitle(mode === 'copy' ? copyTitle(initial.title) : initial.title);
      setDueDate(initial.dueDate || todayIso());
      setBasePointsDisplay(String(initial.basePoints ?? defaultPoints));
      setCategoryId(initial.categoryId ?? '');
      setNotes(initial.notes ?? '');
      setCoverImageId(initial.coverImageId);
      setSelectedIds(new Set(initial.assignedMemberIds));
      setRewardEnabled(!!initial.rewardEnabled);
      setRewardCoinsDisplay(String(initial.rewardCoins ?? 0));
    } else {
      setTitle('');
      setDueDate(todayIso());
      setBasePointsDisplay(String(defaultPoints));
      setCategoryId('');
      setNotes('');
      setCoverImageId(undefined);
      const defaultMembers = defaultGroupId
        ? members.filter((m) => m.groupId === defaultGroupId)
        : members;
      setSelectedIds(new Set(defaultMembers.map((m) => m.id)));
      setRewardEnabled(false);
      setRewardCoinsDisplay('0');
    }
  }, [open, mode, initial, defaultPoints, members, defaultGroupId]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const canSave = title.trim().length > 0 && selectedIds.size > 0 && !!dueDate;
  const heading =
    mode === 'edit' ? '과제 편집하기' : mode === 'copy' ? '복제 과제 만들기' : '일반 과제 생성';

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) ?? null,
    [categories, categoryId]
  );

  if (!open) return null;

  const handleSave = () => {
    if (!canSave) return;
    const now = new Date().toISOString();
    const assignedMemberIds = members.filter((m) => selectedIds.has(m.id)).map((m) => m.id);
    const base: GeneralAssignmentItem = {
      id: mode === 'edit' && initial ? initial.id : newId('gen'),
      kind: 'general',
      title: title.trim(),
      dueDate,
      basePoints: getBasePointsNum(),
      categoryId: categoryId || undefined,
      notes: notes.trim() || undefined,
      coverImageId,
      rewardEnabled,
      rewardCoins: rewardEnabled ? getRewardCoinsNum() : 0,
      rewardItemIds: mode === 'edit' && initial ? initial.rewardItemIds : undefined,
      assignedMemberIds,
      studentStatuses:
        mode === 'edit' && initial
          ? initial.studentStatuses.filter((s) => assignedMemberIds.includes(s.studentId))
          : [],
      createdAt: mode === 'edit' && initial ? initial.createdAt : now,
      updatedAt: now,
    };
    onSave(base);
  };

  return createPortal(
    <div className="cp-apn-create-overlay" role="presentation">
      <div
        className="cp-apn-create-overlay-dim"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="cp-apn-create-overlay-scroll"
        role="presentation"
        onClick={onClose}
      >
        <div
          className="cp-apn-create-modal"
          role="dialog"
          aria-modal="true"
          aria-label={heading}
          onClick={(e) => e.stopPropagation()}
        >
        <h2 className="cp-apn-create-title">{heading}</h2>

        {/* 섹션 1: 커버 이미지 · 이름·설명 */}
        <AssignmentCoverPickField
          coverImageId={coverImageId}
          coverImages={coverImages}
          onCoverImageIdChange={setCoverImageId}
          onCoverImagesChange={onCoverImagesChange}
        >
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">과제 이름</label>
            <input
              className="cp-apn-create-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 일기 쓰기"
            />
          </div>
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">과제 설명 (선택사항)</label>
            <textarea
              className="cp-apn-create-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="과제에 대한 추가 설명이나 메모를 입력하세요..."
              rows={3}
            />
          </div>
        </AssignmentCoverPickField>

        {/* 섹션 2: 보상 토글 */}
        <section
          className="cp-apn-create-section cp-apn-create-section--compact"
          data-tutorial-apn-create="reward"
        >
          <div className="cp-apn-create-toggle-row">
            <span className="cp-apn-create-label">과제 완료 보상</span>
            <button
              type="button"
              className={`cp-apn-create-toggle${rewardEnabled ? ' is-on' : ''}`}
              aria-pressed={rewardEnabled}
              onClick={() => setRewardEnabled((v) => !v)}
            >
              <span className="cp-apn-create-toggle-knob">{rewardEnabled ? 'ON' : 'OFF'}</span>
            </button>
          </div>
          {rewardEnabled ? (
            <div className="cp-apn-create-reward-body">
              <div className="cp-apn-create-field">
                <div className="cp-apn-create-label-row">
                  <label className="cp-apn-create-label">획득 코인</label>
                  <div className="cp-apn-create-inc">
                    <button
                      type="button"
                      className="cp-apn-create-inc-btn"
                      aria-label="코인 초기화"
                      onClick={() => setRewardCoinsDisplay('0')}
                    >
                      <IconReset />
                    </button>
                    <button
                      type="button"
                      className="cp-apn-create-inc-btn"
                      aria-label="코인 빼기"
                      onClick={() =>
                        setRewardCoinsDisplay(String(Math.max(0, getRewardCoinsNum() - 10)))
                      }
                    >
                      <IconMinus />
                    </button>
                    <button
                      type="button"
                      className="cp-apn-create-inc-btn"
                      aria-label="코인 더하기"
                      onClick={() => setRewardCoinsDisplay(String(getRewardCoinsNum() + 10))}
                    >
                      <IconPlus />
                    </button>
                  </div>
                </div>
                <input
                  className="cp-apn-create-input"
                  type="number"
                  min={0}
                  value={rewardCoinsDisplay}
                  onChange={(e) => setRewardCoinsDisplay(e.target.value)}
                  onBlur={() => {
                    if (rewardCoinsDisplay === '') setRewardCoinsDisplay('0');
                  }}
                />
              </div>
            </div>
          ) : null}
        </section>

        {/* 섹션 3: 날짜·포인트·카테고리 */}
        <section className="cp-apn-create-section" data-tutorial-apn-create="schedule">
          <div className="cp-apn-create-field-row">
            <div className="cp-apn-create-field">
              <div className="cp-apn-create-label-row">
                <label className="cp-apn-create-label">마감 날짜</label>
                <div className="cp-apn-create-inc">
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="날짜 초기화"
                    onClick={() => setDueDate(todayIso())}
                  >
                    <IconReset />
                  </button>
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="하루 빼기"
                    onClick={() => setDueDate(shiftDateIso(dueDate, -1))}
                  >
                    <IconMinus />
                  </button>
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="하루 더하기"
                    onClick={() => setDueDate(shiftDateIso(dueDate, 1))}
                  >
                    <IconPlus />
                  </button>
                </div>
              </div>
              <div className="cp-apn-create-date-wrap">
                {dueDate ? (
                  <span className="cp-apn-create-weekday">{formatDueBadge(dueDate)}</span>
                ) : null}
                <input
                  className="cp-apn-create-date-input"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>
            <div className="cp-apn-create-field">
              <div className="cp-apn-create-label-row">
                <label className="cp-apn-create-label">획득 포인트</label>
                <div className="cp-apn-create-inc">
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="포인트 초기화"
                    onClick={() => setBasePointsDisplay('0')}
                  >
                    <IconReset />
                  </button>
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="포인트 빼기"
                    onClick={() =>
                      setBasePointsDisplay(
                        String(Math.max(0, getBasePointsNum() - (defaultPoints || 1)))
                      )
                    }
                  >
                    <IconMinus />
                  </button>
                  <button
                    type="button"
                    className="cp-apn-create-inc-btn"
                    aria-label="포인트 더하기"
                    onClick={() =>
                      setBasePointsDisplay(String(getBasePointsNum() + (defaultPoints || 1)))
                    }
                  >
                    <IconPlus />
                  </button>
                </div>
              </div>
              <input
                className="cp-apn-create-input"
                type="number"
                min={0}
                value={basePointsDisplay}
                onChange={(e) => setBasePointsDisplay(e.target.value)}
                onBlur={() => {
                  if (basePointsDisplay === '') setBasePointsDisplay('0');
                }}
              />
            </div>
          </div>

          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label is-center">카테고리</label>
            <div className="cp-apn-create-cat">
              <button
                type="button"
                className="cp-apn-create-cat-btn"
                onClick={() => setCategoryOpen((v) => !v)}
                aria-expanded={categoryOpen}
              >
                <span
                  className="cp-apn-create-cat-dot"
                  style={{
                    background: selectedCategory?.color || 'rgba(162, 162, 162, 1)',
                  }}
                />
                <span>{selectedCategory?.name || '선택 안 함'}</span>
                <span className="cp-apn-create-cat-chevron" aria-hidden>
                  ▾
                </span>
              </button>
              {categoryOpen ? (
                <div className="cp-apn-create-cat-menu" role="listbox">
                  <button
                    type="button"
                    className={`cp-apn-create-cat-option${!categoryId ? ' is-selected' : ''}`}
                    onClick={() => {
                      setCategoryId('');
                      setCategoryOpen(false);
                    }}
                  >
                    <span
                      className="cp-apn-create-cat-dot"
                      style={{ background: 'rgba(162, 162, 162, 1)' }}
                    />
                    선택 안 함
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`cp-apn-create-cat-option${
                        categoryId === c.id ? ' is-selected' : ''
                      }`}
                      onClick={() => {
                        setCategoryId(c.id);
                        setCategoryOpen(false);
                      }}
                    >
                      <span
                        className="cp-apn-create-cat-dot"
                        style={{ background: c.color }}
                      />
                      {c.name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {/* 섹션 4: 멤버 선택 */}
        <section className="cp-apn-create-section" data-tutorial-apn-create="members">
          <AssignmentMemberPickField
            members={members}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            initialGroupId={defaultGroupId}
            educodiLayout
          />
        </section>

        <div className="cp-apn-create-buttons">
          <button type="button" className="cp-apn-create-btn-cancel" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-apn-create-btn-save"
            disabled={!canSave}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GeneralAssignmentSettingsModal;
